import React from "react";
import PageLayout from "../components/layout/PageLayout";
import PageHero from "../components/shared/PageHero";
import SectionHeading from "../components/shared/SectionHeading";
import CTASection from "../components/shared/CTASection";
import Mandala from "../components/decor/Mandala";
import { PUBLICATIONS } from "../data/publications";
import { IMG } from "../data/site";
import { BookOpen, Star, ScrollText, Users, Landmark } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import mallikaAppQr from "../assets/Mallika_App_QR.png";

const tatvamallikaInitiatives = [
  "Publication of previously unpublished works",
  "Encouragement of research-oriented scholarship and writings",
  "Support for scholars and editors",
  "Preservation of manuscripts",
  "Promotion and expansion of the study of Shastras and philosophy",
];

const tatvamallikaInitiativesKn = [
  "ಹಿಂದೆ ಪ್ರಕಟವಾಗದ ಕೃತಿಗಳ ಪ್ರಕಟಣೆ",
  "ಸಂಶೋಧನಾಧಾರಿತ ವಿದ್ವತ್ತು ಮತ್ತು ಬರವಣಿಗೆಗೆ ಪ್ರೋತ್ಸಾಹ",
  "ವಿದ್ವಾಂಸರು ಮತ್ತು ಸಂಪಾದಕರಿಗೆ ಬೆಂಬಲ",
  "ಹಸ್ತಪ್ರತಿಗಳ ಸಂರಕ್ಷಣೆ",
  "ಶಾಸ್ತ್ರಗಳು ಮತ್ತು ತತ್ತ್ವಶಾಸ್ತ್ರದ ಅಧ್ಯಯನದ ಪ್ರಚಾರ ಮತ್ತು ವಿಸ್ತರಣೆ",
];

const knowledgeTraditions = [
  "Research and publication",
  "Patronage and preservation of traditional scholarship",
  "Indian philosophy and literature",
  "Study of Shastras",
  "Kannada and regional language traditions",
];

const editorialTeam = [
  { role: "Chief Editor", roleKn: "ಪ್ರಧಾನ ಸಂಪಾದಕರು", names: ["Sri P. Srinivasa Tantri, Udupi"] },
  { role: "Editor", roleKn: "ಸಂಪಾದಕರು", names: ["Dr. Gururaja K. Nippani"] },
  {
    role: "Advisory Board",
    roleKn: "ಸಲಹಾ ಮಂಡಳಿ",
    names: [
      "Vidwan Atmakuru Anandatirthacharya",
      "Vidwan Ramesh Acharya",
      "Be. Na. Vijayindra Acharya",
      "Vidwan Raghavendra Puranika",
      "Sri B. Gopalakrishna Upadhyaya",
    ],
  },
  { role: "Management Board", roleKn: "ವ್ಯವಸ್ಥಾಪಕ ಮಂಡಳಿ", names: ["Sri Govindarao V. Navalgund", "Sri A. Ramesh Hatwar"] },
];

const Publications = () => {
  const { t, lang } = useLang();
  const pick = (o, k) => (lang === "kn" && o[`${k}Kn`]) || o[k];
  const featured = PUBLICATIONS.find((p) => p.featured);

  return (
    <PageLayout>
      <PageHero
        eyebrow={t("publications.hero.eyebrow")}
        title={t("publications.hero.title")}
        subtitle={t("publications.hero.subtitle")}
        image={IMG.manuscripts}
      />

      {/* Featured */}
      {featured && (
        <section className="py-16" data-testid="pub-featured">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
            <div className="relative">
              <div className="aspect-[3/4] rounded-3xl overflow-hidden shadow-2xl relative bg-[#3B2A1A]">
                <img
                  src={require("../assets/s3umyq37_Tatvamallika_Publication.webp")}
                  alt="Tatvamallika"
                  className="w-full h-full object-cover"
                />
                {/* Top scrim for title readability */}
                <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-[#3B2A1A]/90 via-[#3B2A1A]/60 to-transparent" />
                {/* Bottom scrim for tag readability */}
                <div className="absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-[#3B2A1A]/85 to-transparent" />
                <div className="absolute inset-0 p-10 flex flex-col justify-between">
                  <div>
                    <Star className="w-8 h-8 text-[#F4CB50]" />
                    <div className="mt-6 overline text-[#F4CB50]">{pick(featured, "type")}</div>
                    <h3 className="mt-2 font-serif-display text-4xl text-[#FBF7F0] drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]">{pick(featured, "title")}</h3>
                  </div>
                  <div>
                    <div className="text-xs text-[#F4CB50]">{pick(featured, "language")}</div>
                    <div className="mt-2 font-cinzel text-xs tracking-widest text-[#FBF7F0]">{lang === "kn" ? "ಮುಖ್ಯ ಪ್ರಕಟಣೆ" : "The flagship publication"}</div>
                  </div>
                </div>
                <div className="absolute -top-8 -right-8 opacity-30"><Mandala size={200} color="#D4AF37" opacity={1} /></div>
              </div>
            </div>
            <div>
              <div className="overline text-[#B65A14] mb-3">{t("publications.featured")}</div>
              <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3B2A1A] leading-tight">{pick(featured, "title")}</h2>
              <div className="mt-4 gold-underline" />
              <p className="mt-6 text-[#3B2A1A]/85 leading-relaxed">{pick(featured, "desc")}</p>
              <p className="mt-4 text-[#3B2A1A]/85 leading-relaxed">{lang === "kn" ? "ಪ್ರತಿ ಸಂಚಿಕೆಯಲ್ಲಿ ದ್ವೈತ ಸಿದ್ಧಾಂತದ ಮೇಲಿನ ಶಾಸ್ತ್ರೀಯ ಲೇಖನಗಳು, ಉತ್ಸವ ವರದಿಗಳು, ಪೀಠಾಧಿಪತಿಗಳ ಪ್ರವಚನಗಳು, ಹಿರಿಯ ವಿದ್ವಾಂಸರ ಕೊಡುಗೆಗಳು ಹಾಗೂ ಮಠದ ಚಟುವಟಿಕೆಗಳ ಇತ್ತೀಚಿನ ಮಾಹಿತಿ ಇರುತ್ತದೆ." : "Every issue features scholarly articles on Dvaita philosophy, festival reports, discourses of the Peethadhipati, contributions from senior Vidwans, and updates from the Matha's activities."}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <button className="inline-flex items-center gap-2 rounded-full bg-[#B65A14] text-[#FBF7F0] px-6 py-3 text-sm font-semibold" data-testid="pub-subscribe">{t("publications.subscribe")}</button>
                <button className="inline-flex items-center gap-2 rounded-full border border-[#B65A14]/40 text-[#B65A14] px-6 py-3 text-sm font-semibold" data-testid="pub-archive">{t("publications.viewArchive")}</button>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="bg-sand-texture py-16 relative overflow-hidden" data-testid="tatvamallika-about">
        <div className="absolute -top-24 -right-24 opacity-25 animate-mandala-slow">
          <Mandala size={340} color="#B65A14" opacity={0.5} />
        </div>
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <SectionHeading
            eyebrow={lang === "kn" ? 'ಶ್ರೀ ಸೋದೆ ವಾದಿರಾಜ ಮಠದ ತ್ರೈಮಾಸಿಕ ಪ್ರಕಟಣೆ' :'A Quarterly Publication of Sri Sode Vadiraja Matha'}
            title={lang === "kn" ? 'ತತ್ವಮಲ್ಲಿಕಾ ಬಗ್ಗೆ' :'About Tatvamallika'}
            subtitle={lang === "kn" ? 'ತತ್ತ್ವಮಲ್ಲಿಕಾವು ದ್ವೈತ ತತ್ತ್ವಶಾಸ್ತ್ರ, ಶಾಸ್ತ್ರೀಯ ವಿದ್ವತ್ತು, ಭಾರತೀಯ ಜ್ಞಾನ ಪರಂಪರೆ, ಕನ್ನಡ ಸಾಹಿತ್ಯ ಪರಂಪರೆ ಹಾಗೂ ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರು ಮತ್ತು ಇತರ ಗೌರವಾನ್ವಿತ ವಿದ್ವಾಂಸರು ಹಾಗೂ ಸಂತರು ರಚಿಸಿದ ಕೃತಿಗಳ ಅಧ್ಯಯನ ಮತ್ತು ಪ್ರಸಾರಕ್ಕೆ ಸಮರ್ಪಿತವಾಗಿದೆ.' :'Tatvamallika is dedicated to the study and propagation of Dvaita philosophy, Shastric learning, the Indian knowledge tradition, Kannada literary heritage, and the works of Sri Vadiraja Theertha and other revered scholars and saints.'}
            align="center"
          />

          <div className="mt-12 grid lg:grid-cols-3 gap-5">
            <div className="bg-[#FBF7F0] border border-[#D4AF37]/30 rounded-2xl p-7 relative overflow-hidden">
              <ScrollText className="w-8 h-8 text-[#B65A14] mb-4" />
              <h3 className="font-serif-display text-2xl text-[#3B2A1A]">
                {lang === "kn" ? "ತ್ರೈಮಾಸಿಕ ಪತ್ರಿಕೆ" : "Quarterly Magazine"}
              </h3>
              <p className="mt-4 text-sm text-[#3B2A1A]/80 leading-relaxed">
                {lang === "kn"
                  ? "ತತ್ವಮಲ್ಲಿಕಾ ಶ್ರೀ ಸೋದೆ ವಾದಿರಾಜ ಮಠದಿಂದ ಪ್ರಕಟವಾಗುವ ತ್ರೈಮಾಸಿಕ ಪತ್ರಿಕೆ. ಇದು ಸಂಶೋಧನೆ, ಪ್ರಕಟಣೆ, ವಿದ್ವತ್ತಿನ ಪ್ರೋತ್ಸಾಹ ಮತ್ತು ತತ್ತ್ವಜ್ಞಾನದ ಅಧಿಕೃತ ಪ್ರಸಾರದ ಕುರಿತ ಮಠದ ದೀರ್ಘಕಾಲದ ದೃಷ್ಟಿಕೋನವನ್ನು ಪ್ರತಿಬಿಂಬಿಸುತ್ತದೆ."
                  : "Tatvamallika is a quarterly magazine published by Sri Vadiraja Matha, Sode. It reflects the Matha’s long-standing vision of research, publication, promotion of scholarship, and the authentic dissemination of philosophical knowledge."}
              </p>
              <p className="mt-4 text-sm text-[#3B2A1A]/80 leading-relaxed">
                {lang === "kn"
                  ? "ತತ್ವಮಲ್ಲಿಕೆಯಲ್ಲಿ ಸಂಶೋಧನಾಧಾರಿತ ಲೇಖನಗಳು, ವಿದ್ವತ್ಪೂರ್ಣ ಬರಹಗಳು, ಆಧ್ಯಾತ್ಮಿಕ ಚಿಂತನೆಗಳು ಹಾಗೂ ಓದುಗರಿಗೆ ಮೌಲ್ಯಯುತ ಮತ್ತು ಆಸಕ್ತಿದಾಯಕ ವಿಷಯಗಳು ಪ್ರಕಟವಾಗುತ್ತವೆ."
                  : "Tatvamallika features research-oriented articles, scholarly writings, spiritual reflections, and subjects of value and interest to its readers."}
              </p>
            </div>

            <div className="bg-[#FBF7F0] border border-[#D4AF37]/30 rounded-2xl p-7 relative overflow-hidden">
              <BookOpen className="w-8 h-8 text-[#B65A14] mb-4" />
              <h3 className="font-serif-display text-2xl text-[#3B2A1A]">
                {lang === "kn" ? "ಮಠದ ಉಪಕ್ರಮಗಳು" : "Matha Initiatives"}
              </h3>
              <p className="mt-4 text-sm text-[#3B2A1A]/80 leading-relaxed">
                {lang === "kn"
                  ? "ಈ ಪತ್ರಿಕೆ ಮಠದ ಹಲವು ಪ್ರಮುಖ ಉಪಕ್ರಮಗಳೊಂದಿಗೆ ನಿಕಟವಾಗಿ ಸಂಬಂಧಿಸಿದೆ, ಅವುಗಳಲ್ಲಿ:"
                  : "The magazine is closely associated with several important initiatives of the Matha, including:"}
              </p>
              <ul className="mt-4 list-disc pl-5 space-y-2 text-sm text-[#3B2A1A]/80 leading-relaxed">
                {(lang === "kn" ? tatvamallikaInitiativesKn : tatvamallikaInitiatives).map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="bg-[#FBF7F0] border border-[#D4AF37]/30 rounded-2xl p-7 relative overflow-hidden">
              <Landmark className="w-8 h-8 text-[#B65A14] mb-4" />
              <h3 className="font-serif-display text-2xl text-[#3B2A1A]">
                {lang === "kn" ? "ಸಂಶೋಧನಾ ದಿಕ್ಕು" : "Research Direction"}
              </h3>
              <p className="mt-4 text-sm text-[#3B2A1A]/80 leading-relaxed">
                {lang === "kn"
                  ? "ಪೂಜ್ಯ ಸ್ವಾಮೀಜಿಗಳ ದೃಷ್ಟಿಕೋನಕ್ಕೆ ಅನುಗುಣವಾಗಿ, ತತ್ವಮಲ್ಲಿಕಾ ಮುಂದಿನ ವರ್ಷಗಳಲ್ಲಿ ವಿದ್ವತ್ಪೂರ್ಣ ಮತ್ತು ಜ್ಞಾನಾಧಾರಿತ ಕೊಡುಗೆಗಳಿಗೆ ಹೆಚ್ಚಿನ ಒತ್ತು ನೀಡುತ್ತಾ, ಇನ್ನಷ್ಟು ಸಂಶೋಧನಾಮುಖಿ ಪ್ರಕಟಣೆಯಾಗಿ ಕ್ರಮೇಣ ಬೆಳೆಯುತ್ತಿದೆ."
                  : "In accordance with the vision of the revered Swamiji, Tatvamallika is steadily evolving into an increasingly research-oriented publication, with a greater emphasis on scholarly and knowledge-based contributions in the years ahead."}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#FFF9EE]" data-testid="tatvamallika-editorial-team">
        <div className="max-w-5xl mx-auto px-4 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#F2E7D5] text-[#B65A14] mb-4">
              <Users className="w-5 h-5" />
            </div>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3B2A1A] leading-tight">
              {lang === "kn" ? "ಸಂಪಾದಕೀಯ ಮತ್ತು ಸಲಹಾ ತಂಡ" : "Editorial & Advisory Team"}
            </h2>
            <div className="mt-5 mx-auto h-1 w-24 rounded-full bg-gradient-to-r from-transparent via-[#D99A22] to-transparent" />
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {editorialTeam.map((group) => (
              <div key={group.role} className="bg-white border border-[#D4AF37]/25 border-t-4 border-t-[#D99A22] rounded-2xl p-7 sm:p-8 shadow-sm min-h-[168px]">
                <h3 className="font-serif-display text-2xl text-[#D99A22]">
                  {lang === "kn" ? group.roleKn : group.role}
                </h3>
                {group.names.length > 1 ? (
                  <ul className="mt-5 list-disc pl-5 space-y-2 text-[#3B2A1A]/85 leading-relaxed">
                    {group.names.map((name) => (
                      <li key={name}>{name}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-5 text-[#3B2A1A]/85 leading-relaxed">{group.names[0]}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-ivory-texture relative overflow-hidden" data-testid="pub-app-download">
        <div className="absolute -left-28 -top-28 opacity-[0.18] pointer-events-none">
          <Mandala size={360} color="#D4AF37" opacity={0.8} />
        </div>
        <div className="absolute -right-24 -top-20 opacity-[0.18] pointer-events-none">
          <Mandala size={390} color="#D4AF37" opacity={0.75} />
        </div>
        <div className="absolute -right-32 -bottom-32 opacity-[0.16] pointer-events-none">
          <Mandala size={440} color="#D4AF37" opacity={0.75} />
        </div>

        <div className="max-w-[1312px] mx-auto px-5 sm:px-8 lg:px-10 relative">
          <div className="min-h-[490px] rounded-[30px] border-[3px] border-[#ECD58D] bg-[#FBF7F0]/45 shadow-[0_26px_80px_rgba(92,66,33,0.08)] px-7 py-10 sm:px-12 lg:px-[52px] lg:py-[52px] grid lg:grid-cols-[minmax(0,1fr)_390px] gap-12 lg:gap-[96px] items-center">
            <div className="max-w-[600px]">
              <div className="overline text-[#C65D1B] mb-6">
                {lang === "kn" ? "ಮೊಬೈಲ್ ಆಪ್" : "Mobile App"}
              </div>
              <h2 className="font-serif-display text-[42px] sm:text-[52px] lg:text-[56px] text-[#4B3828] leading-[1.05]">
                {lang === "kn" ? "ಅಪ್ ಡೌನ್‌ಲೋಡ್ ಮಾಡಿ" : "Download the App"}
              </h2>
              <div className="mt-6 h-px w-[150px] bg-gradient-to-r from-[#E5C765] to-transparent" />
              <p className="mt-8 text-[20px] leading-[1.6] text-[#5E5247]">
                {lang === "kn"
                  ? "ಪ್ರವಚನಗಳು, ಮಾಸಪತ್ರಿಕೆ ಸಂಚಿಕೆಗಳು, ಗ್ರಂಥಗಳು ಮತ್ತು ಮಠದ ನೇರ ನವೀಕರಣಗಳನ್ನು ತತ್ವಮಲ್ಲಿಕಾ ಆಪ್ ಮೂಲಕ ನಿಮ್ಮ ಬೆರಳ ತುದಿಯಲ್ಲಿ ಪಡೆಯಿರಿ."
                  : "Access discourses, magazine issues, granthas and the Matha's live updates at your fingertips through the Tatvamallika app."}
              </p>
              <p className="mt-6 text-[18px] leading-relaxed font-bold text-[#C65D1B]">
                {lang === "kn"
                  ? "ಸ್ಥಾಪಿಸಲು ನಿಮ್ಮ ಫೋನ್ ಕ್ಯಾಮೆರಾದಿಂದ QR ಕೋಡ್ ಸ್ಕ್ಯಾನ್ ಮಾಡಿ"
                  : "Scan the QR code with your phone camera to install"}
              </p>
            </div>

            <div className="justify-self-center lg:justify-self-end">
              <div className="w-[280px] sm:w-[360px] lg:w-[384px] aspect-square rounded-[24px] border-2 border-[#ECD58D] bg-[#FBF7F0]/90 shadow-[0_18px_24px_rgba(59,42,26,0.18)] flex items-center justify-center p-9">
                <div className="w-full aspect-square overflow-hidden">
                  <img
                    src={mallikaAppQr}
                    alt={lang === "kn" ? "ತತ್ವಮಲ್ಲಿಕಾ ಆಪ್ QR ಕೋಡ್" : "Tatvamallika app QR code"}
                    className="w-full h-auto max-w-none "
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* <SectionDivider variant="mandala" /> */}

      {/* Library */}
      {/* <section className="pb-24" data-testid="pub-library">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
            <SectionHeading eyebrow={t("publications.explore.eyebrow")} title={t("publications.explore.title")} />
            <div className="flex items-center gap-3 bg-[#FBF7F0] border border-[#D4AF37]/30 rounded-full px-5 py-3 w-full md:w-80">
              <Search className="w-4 h-4 text-[#B65A14]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={lang === "kn" ? "ಪ್ರಕಟಣೆಗಳನ್ನು ಹುಡುಕಿ…" : "Search publications…"} className="bg-transparent outline-none flex-1 text-sm text-[#3B2A1A]" data-testid="pub-search" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((p) => (
              <div key={p.title} className="relative bg-gradient-to-br from-[#F2E7D5] to-[#FBF7F0] border border-[#D4AF37]/40 rounded-2xl p-7 card-lift overflow-hidden" data-testid={`pub-${p.title.toLowerCase().replace(/\W/g, "-")}`}>
                <div className="absolute -top-6 -right-6 opacity-15"><Mandala size={100} color="#B65A14" opacity={1} /></div>
                <BookOpen className="w-8 h-8 text-[#B65A14] mb-4" />
                <div className="overline text-[#B65A14]">{pick(p, "type")}</div>
                <h3 className="mt-2 font-serif-display text-xl text-[#3B2A1A] leading-tight">{pick(p, "title")}</h3>
                <p className="mt-3 text-sm text-[#3B2A1A]/80 leading-relaxed">{pick(p, "desc")}</p>
                <div className="mt-5 flex items-center justify-between text-xs">
                  <span className="text-[#3B2A1A]/60 italic">{pick(p, "language")}</span>
                  <button className="inline-flex items-center gap-1 text-[#B65A14] font-semibold" data-testid={`pub-download-${p.title.slice(0, 10).toLowerCase()}`}><Download className="w-3.5 h-3.5" /> {lang === "kn" ? "ವೀಕ್ಷಿಸಿ" : "View"}</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      <CTASection
        eyebrow={lang === "kn" ? "ವಿದ್ವತ್ತೆಗೆ ಬೆಂಬಲ ನೀಡಿ" : "Support scholarship"}
        title={lang === "kn" ? "ಶಾಸ್ತ್ರೀಯ ಗ್ರಂಥದ ವಿಮರ್ಶಾತ್ಮಕ ಆವೃತ್ತಿಗೆ ದೇಣಿಗೆ ನೀಡಿ" : "Fund a critical edition of a classical work"}
        primary={{ label: lang === "kn" ? "ಪ್ರಕಟಣಾ ಸೇವೆ" : "Publication Seva", to: "/sevas" }}
        secondary={{ label: lang === "kn" ? "ಸಂಶೋಧನೆ" : "Research", to: "/publications" }}
        image={IMG.library}
      />
    </PageLayout>
  );
};

export default Publications;
